// UAUCAllPanel.jsx
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";

// --- helpers ---
async function safeFetchJSON(url, { signal } = {}) {
  const res = await fetch(url, { signal });
  const data = await res.json().catch(() => null);
  if (!res.ok)
    throw new Error(
      data?.message || data?.error || `Request failed (${res.status})`
    );
  return data;
}

function normalizeHazards(hazards) {
  return Array.isArray(hazards)
    ? hazards
        .map((h) => h?.hazards || h?.name || h)
        .filter(Boolean)
        .join(", ")
    : "";
}

function parseDateTimeBoundary(input, endOfDay = false) {
  if (!input) return null;
  const d = new Date(
    /\d{1,2}:\d{2}/.test(input)
      ? input
      : `${input}T${endOfDay ? "23:59:59" : "00:00:00"}`
  );
  return isNaN(d.getTime()) ? null : d;
}

function getNestedValue(obj, path) {
  return path.split(".").reduce((acc, key) => {
    if (acc && typeof acc === "object" && key in acc) {
      return acc[key];
    }
    return undefined;
  }, obj);
}


// --- main ---
export default function UAUCAllPanel({
  apiBase,
  projectId,
  onBack,
  title = "All Observations",
}) {
  const [data, setData] = useState([]);
  const [resolvedData, setResolvedData] = useState([]);
  const [query, setQuery] = useState("");
  const [assignedToId, setAssignedToId] = useState("");
  const [advOpen, setAdvOpen] = useState(false);
  const [ratings, setRatings] = useState({});
const [adv, setAdv] = useState({
  from: "",
  to: "",
  area: "",
  minRisk: "",
  maxRisk: "",
  status: [], // ['open','closed','resolved']
  severity: [], // ['Low','Moderate','High','Critical'] (whatever you use)
  overdueOnly: false,
  lateOnly: false, // closed late
  onlyOpen: false,
  onlyClosed: false,
  assignees: [], // multi-select of assignee IDs
});

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const abortRef = useRef(null);
  const navigate = useNavigate();
  const OBSERVER_BOXSHOW = ["userId", "designation", "emailId", "phone"];

  // --- stable helpers ---
  const getOpenStartDate = useCallback(
    (it) => it?.openedAt || it?.date || it?.createdAt,
    []
  );
  // --- stable helpers ---
  // keep getOpenStartDate and formatDuration as-is

  const getAssigneeIds = useCallback((it) => {
    if (!it?.assignedTo) return [];
    if (Array.isArray(it.assignedTo)) {
      return it.assignedTo
        .map((a) => (typeof a === "object" ? a?._id : a))
        .filter(Boolean);
    }
    return [
      typeof it.assignedTo === "object" ? it.assignedTo?._id : it.assignedTo,
    ].filter(Boolean);
  }, []);

  const getAssigneeNameById = useCallback((it, id) => {
    if (!id) return "";
    const arr = Array.isArray(it?.assignedTo)
      ? it.assignedTo
      : [it?.assignedTo].filter(Boolean);
    const found = arr.find((a) => (typeof a === "object" ? a?._id : a) === id);
    return typeof found === "object" ? found?.name || "" : "";
  }, []);

  const getAssigneeObjById = useCallback((it, id) => {
    if (!id) return null;
    const arr = Array.isArray(it?.assignedTo)
      ? it.assignedTo
      : [it?.assignedTo].filter(Boolean);
    const found = arr.find((a) => (typeof a === "object" ? a?._id : a) === id);
    return typeof found === "object" ? found : null;
  }, []);

  const getSlaHours = useCallback((it) => {
    const rv = it?.riskValue || {};
    const base = Number(rv.alertTimeline) || 0;
    const escalations = Array.isArray(rv.escalationAlert)
      ? rv.escalationAlert.length
      : 0;
    return base * (escalations || 1);
  }, []);

  const formatDuration = useCallback((ms) => {
    if (!Number.isFinite(ms)) return "—";
    const d = Math.floor(ms / 86400000);
    const h = Math.floor((ms % 86400000) / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`;
  }, []);

  const getDueAt = useCallback(
    (it) => {
      const opened = getOpenStartDate(it);
      return opened
        ? new Date(new Date(opened).getTime() + getSlaHours(it) * 3600 * 1000)
        : null;
    },
    [getOpenStartDate, getSlaHours]
  );

  // ⏱ SLA meta for filtering (overdue / late / durations)
  const getDueMeta = (it) => {
    const opened = it?.openedAt || it?.date || it?.createdAt;
    if (!opened) return { dueAt: null, overdue: false, closedLate: false };

    const slaHrs = getSlaHours(it);
    if (!slaHrs) return { dueAt: null, overdue: false, closedLate: false };

    const dueAt = new Date(new Date(opened).getTime() + slaHrs * 3600 * 1000);
    const isClosed = /closed|resolved/i.test(it?.status);
    if (isClosed) {
      const closedAt =
        it?.closedOn || it?.closedAt || it?.resolvedAt || it?.updatedAt;
      const closedLate = closedAt
        ? new Date(closedAt).getTime() > dueAt.getTime()
        : false;
      return { dueAt, overdue: false, closedLate };
    }
    return { dueAt, overdue: Date.now() > dueAt.getTime(), closedLate: false };
  };

  // --- fetch data ---
  useEffect(() => {
    const ac = new AbortController();
    abortRef.current = ac;

    (async () => {
      setLoading(true);
      setErr("");
      try {
        const url = new URL(apiBase + "/uauc");
        url.searchParams.set("project", projectId);
        const res = await safeFetchJSON(url.toString(), { signal: ac.signal });

        let list = Array.isArray(res) ? res : res?.data || [];
        list = list.filter(
          (it) => String(it?.project?._id || it?.project) === String(projectId)
        );

        setData(list);
        setResolvedData(
          list.filter((it) => /closed|resolved/i.test(it?.status))
        );

        // inside the same useEffect after `setResolvedData(...)`
        const uniqueIds = [
          ...new Set(list.flatMap((it) => getAssigneeIds(it)).filter(Boolean)),
        ];
        if (uniqueIds.length) {
          const ratingRes = await Promise.allSettled(
            uniqueIds.map((id) =>
              safeFetchJSON(`${apiBase}/uauc/rating/${id}`, {
                signal: ac.signal,
              })
            )
          );
          const ratingMap = {};
          ratingRes.forEach((r, i) => {
            if (r.status === "fulfilled") ratingMap[uniqueIds[i]] = r.value;
          });
          setRatings(ratingMap);
        }
      } catch (e) {
        if (e.name !== "AbortError") setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();

    return () => ac.abort();
  }, [apiBase, projectId, getAssigneeIds]);

  // --- filter + sort ---
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return data
      .filter((it) => {
        // 🔎 Text search
        if (q) {
          const text = [
            it?.observer?.name,
            it?.area,
            normalizeHazards(it?.hazards),
            it?.observation,
            it?.status,
            it?.riskValue?.severity,
          ]
            .join(" ")
            .toLowerCase();
          if (!text.includes(q)) return false;
        }

        // 🎯 Top-bar single assignee filter
        if (assignedToId) {
          const ids = getAssigneeIds(it).map(String);
          if (!ids.includes(String(assignedToId))) return false;
        }

        // 👥 Advanced: assignees multi-select
        if (adv.assignees?.length) {
          const ids = getAssigneeIds(it).map(String);
          const anyMatch = adv.assignees.some((x) => ids.includes(String(x)));
          if (!anyMatch) return false;
        }

        // 📅 Date range on "opened"
        const opened = getOpenStartDate(it);
        const t = opened ? new Date(opened).getTime() : 0;
        if (adv.from && t < (parseDateTimeBoundary(adv.from)?.getTime() ?? 0))
          return false;
        if (
          adv.to &&
          t > (parseDateTimeBoundary(adv.to, true)?.getTime() ?? Infinity)
        )
          return false;

        // 🗺 Area contains
        if (
          adv.area &&
          !String(it?.area || "")
            .toLowerCase()
            .includes(adv.area.toLowerCase())
        ) {
          return false;
        }

        // 📈 Risk range
        const riskVal = Number(it?.riskValue?.value ?? NaN);
        if (
          adv.minRisk &&
          !Number.isNaN(riskVal) &&
          riskVal < Number(adv.minRisk)
        )
          return false;
        if (
          adv.maxRisk &&
          !Number.isNaN(riskVal) &&
          riskVal > Number(adv.maxRisk)
        )
          return false;

        // 🎚 Severity (multi)
        if (adv.severity?.length) {
          const sev = String(it?.riskValue?.severity || "").toLowerCase();
          const ok = adv.severity.some((s) => sev === String(s).toLowerCase());
          if (!ok) return false;
        }

        // 🏷 Status (multi) + open/closed toggles
        const status = String(it?.status || "").toLowerCase();
        if (adv.status?.length) {
          const ok = adv.status.some((s) =>
            status.includes(String(s).toLowerCase())
          );
          if (!ok) return false;
        }
        if (adv.onlyOpen && /closed|resolved/i.test(status)) return false;
        if (adv.onlyClosed && !/closed|resolved/i.test(status)) return false;

        // ⏱ Overdue / Closed Late using SLA
        const meta = getDueMeta(it);
        if (adv.overdueOnly && !meta.overdue) return false; // applies only to open items
        if (adv.lateOnly && !meta.closedLate) return false; // applies only to closed items

        return true;
      })

      .sort(
        (a, b) =>
          new Date(getOpenStartDate(a) || 0) -
          new Date(getOpenStartDate(b) || 0)
      );
  }, [data, query, assignedToId, adv, getOpenStartDate]);

  // add this memo near state hooks
  const assigneeOptions = useMemo(() => {
    const map = new Map(); // id -> name
    for (const it of data) {
      for (const id of getAssigneeIds(it)) {
        if (!map.has(id)) {
          const name = getAssigneeNameById(it, id) || "Unnamed";
          map.set(id, name);
        }
      }
    }
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [data, getAssigneeIds, getAssigneeNameById]);

  // --- group by assignee ---
  // --- group by assignee ---
  const groupedByAssignee = useMemo(() => {
    const groups = new Map();

    const addToGroup = (id, name, item) => {
      if (!groups.has(id))
        groups.set(id, { id, name: name || "Unassigned", items: [] });
      groups.get(id).items.push(item);
    };

    for (const it of filtered) {
      const ids = getAssigneeIds(it);
      if (ids.length === 0) {
        addToGroup("__unassigned__", "Unassigned", it);
      } else {
        ids.forEach((id) => addToGroup(id, getAssigneeNameById(it, id), it));
      }
    }
    return [...groups.values()];
  }, [filtered, getAssigneeIds, getAssigneeNameById]);

  const openEdit = useCallback(
    (it) => it?._id && navigate(`/edit/uauc/${it._id}`),
    [navigate]
  );

  // --- render ---
  return (
    <div>
      {/* top bar */}
      <div className="mb-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h4 className="text-base font-semibold">
          {title}
          <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700 ring-1 ring-blue-200">
            {filtered.length}
          </span>
        </h4>
        <div className="flex flex-wrap gap-2">
          <input
            className="w-56 rounded-xl border px-3 py-2 text-sm"
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {/* replace your <select>’s options block */}
          <select
            value={assignedToId}
            onChange={(e) => setAssignedToId(e.target.value)}
            className="rounded-xl border px-2 py-2 text-sm"
          >
            <option value="">All Assignees</option>
            {assigneeOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>

          <button
            onClick={() => setAdvOpen((v) => !v)}
            className="rounded-xl border px-3 py-2 text-sm"
          >
            {advOpen ? "Close Filters" : "Advanced Filters"}
          </button>
          {onBack && (
            <button
              onClick={onBack}
              className="rounded-xl border px-3 py-2 text-sm"
            >
              ← Back
            </button>
          )}
        </div>
      </div>

      {/* Advanced filters */}
      {advOpen && (
        <div className="mb-3 grid gap-3 rounded-xl border bg-gray-50 p-3 sm:grid-cols-2 lg:grid-cols-4">
          {/* Date range (Opened) */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">From (Opened)</label>
            <input
              type="date"
              className="rounded-lg border bg-white px-2 py-1 text-sm"
              value={adv.from}
              onChange={(e) => setAdv((s) => ({ ...s, from: e.target.value }))}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">To (Opened)</label>
            <input
              type="date"
              className="rounded-lg border bg-white px-2 py-1 text-sm"
              value={adv.to}
              onChange={(e) => setAdv((s) => ({ ...s, to: e.target.value }))}
            />
          </div>

          {/* Area contains */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">Area contains</label>
            <input
              className="rounded-lg border bg-white px-2 py-1 text-sm"
              placeholder="e.g. TG Deck"
              value={adv.area}
              onChange={(e) => setAdv((s) => ({ ...s, area: e.target.value }))}
            />
          </div>

          {/* Risk range */}
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <label className="text-xs text-gray-600">Min Risk</label>
              <input
                type="number"
                className="w-full rounded-lg border bg-white px-2 py-1 text-sm"
                value={adv.minRisk}
                onChange={(e) =>
                  setAdv((s) => ({ ...s, minRisk: e.target.value }))
                }
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-gray-600">Max Risk</label>
              <input
                type="number"
                className="w-full rounded-lg border bg-white px-2 py-1 text-sm"
                value={adv.maxRisk}
                onChange={(e) =>
                  setAdv((s) => ({ ...s, maxRisk: e.target.value }))
                }
              />
            </div>
          </div>

          {/* Status multi-select */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">Status</label>
            <select
              multiple
              className="rounded-lg border bg-white px-2 py-1 text-sm h-24"
              value={adv.status}
              onChange={(e) =>
                setAdv((s) => ({
                  ...s,
                  status: Array.from(e.target.selectedOptions, (o) => o.value),
                }))
              }
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
              <option value="resolved">Resolved</option>
              <option value="in-progress">In-Progress</option>
            </select>
          </div>

          {/* Severity multi-select (match your data values) */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">Severity</label>
            <select
              multiple
              className="rounded-lg border bg-white px-2 py-1 text-sm h-24"
              value={adv.severity}
              onChange={(e) =>
                setAdv((s) => ({
                  ...s,
                  severity: Array.from(
                    e.target.selectedOptions,
                    (o) => o.value
                  ),
                }))
              }
            >
              <option value="Low">Low</option>
              <option value="Moderate">Moderate</option>
              <option value="High">High</option>
              <option value="Critical">Critical</option>
            </select>
          </div>

          {/* Assignees multi-select */}
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-600">Assignees</label>
            <select
              multiple
              className="rounded-lg border bg-white px-2 py-1 text-sm h-24"
              value={adv.assignees}
              onChange={(e) =>
                setAdv((s) => ({
                  ...s,
                  assignees: Array.from(
                    e.target.selectedOptions,
                    (o) => o.value
                  ),
                }))
              }
            >
              {assigneeOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>

          {/* Toggles */}
          <div className="flex flex-col gap-2">
            <label className="text-xs text-gray-600">Quick Toggles</label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={adv.onlyOpen}
                onChange={(e) =>
                  setAdv((s) => ({
                    ...s,
                    onlyOpen: e.target.checked,
                    onlyClosed: e.target.checked ? false : s.onlyClosed,
                  }))
                }
              />
              Only Open
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={adv.onlyClosed}
                onChange={(e) =>
                  setAdv((s) => ({
                    ...s,
                    onlyClosed: e.target.checked,
                    onlyOpen: e.target.checked ? false : s.onlyOpen,
                  }))
                }
              />
              Only Closed
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={adv.overdueOnly}
                onChange={(e) =>
                  setAdv((s) => ({ ...s, overdueOnly: e.target.checked }))
                }
              />
              Overdue Only
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={adv.lateOnly}
                onChange={(e) =>
                  setAdv((s) => ({ ...s, lateOnly: e.target.checked }))
                }
              />
              Closed Late Only
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-end gap-2">
            <button
              className="rounded-lg border px-3 py-2 text-sm bg-white"
              onClick={() =>
                setAdv({
                  from: "",
                  to: "",
                  area: "",
                  minRisk: "",
                  maxRisk: "",
                  status: [],
                  severity: [],
                  overdueOnly: false,
                  lateOnly: false,
                  onlyOpen: false,
                  onlyClosed: false,
                  assignees: [],
                })
              }
            >
              Reset
            </button>
            <div className="text-xs text-gray-500">
              {[
                adv.from && `From ${adv.from}`,
                adv.to && `To ${adv.to}`,
                adv.area && `Area "${adv.area}"`,
                (adv.minRisk || adv.maxRisk) &&
                  `Risk ${adv.minRisk || 0}–${adv.maxRisk || "∞"}`,
                adv.status.length && `Status: ${adv.status.join(", ")}`,
                adv.severity.length && `Severity: ${adv.severity.join(", ")}`,
                adv.assignees.length && `Assignees: ${adv.assignees.length}`,
                adv.onlyOpen && "Only Open",
                adv.onlyClosed && "Only Closed",
                adv.overdueOnly && "Overdue Only",
                adv.lateOnly && "Closed Late Only",
              ]
                .filter(Boolean)
                .join(" · ")}
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div>Loading…</div>
      ) : err ? (
        <div className="text-red-600">{err}</div>
      ) : (
        groupedByAssignee.map((section) => (
          <div
            key={section.id}
            className="overflow-hidden rounded-2xl border shadow-sm mb-6"
          >
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between bg-gray-50 px-4 py-3 rounded-t-2xl border-b">
              {/* Left side: name + details */}
              <div className="flex flex-col gap-2">
                <h5 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  👤 {section.name}
                  <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                    {section.items.length} UAUCs
                  </span>
                </h5>

                {/* 🔹 User details if available */}
                {/* inside the section header's left side */}
                {(() => {
                  const sample = section.items?.[0];
                  const assigneeObj =
                    section.id === "__unassigned__"
                      ? null
                      : getAssigneeObjById(sample, section.id);

                  if (!assigneeObj) return null;

                  return (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-xs text-gray-600">
                      {OBSERVER_BOXSHOW.map((fieldKey) => {
                        const value = assigneeObj[fieldKey];
                        if (!value) return null;
                        return (
                          <li
                            key={fieldKey}
                            className="flex items-center gap-1.5 bg-white rounded-md px-2 py-1 shadow-sm border border-gray-200"
                          >
                            <span className="font-medium text-gray-500">
                              {fieldKey}:
                            </span>
                            <span className="text-gray-800 break-words">
                              {value}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                  );
                })()}
              </div>

              {/* Right side: Ratings */}
              {(() => {
                const r = ratings[section.id];
                if (!r) {
                  return (
                    <span className="text-xs text-gray-400 self-end">
                      Loading rating…
                    </span>
                  );
                }

                // ---- SAFE HELPERS ----
                const num = (v, d = 0) => {
                  if (typeof v === "string") {
                    const m = v.match(/-?\d+(\.\d+)?/);
                    const n = m ? parseFloat(m[0]) : NaN;
                    return Number.isFinite(n) ? n : d;
                  }
                  const n = Number(v);
                  return Number.isFinite(n) ? n : d;
                };
                const pct = (nVal, dVal) => {
                  const n = num(nVal);
                  const d = num(dVal);
                  if (d <= 0) return 0;
                  const p = (n / d) * 100;
                  return Math.min(100, Math.max(0, p));
                };

                // ---- INPUTS (coerced) ----
                const openCount = num(r.openCount);
                const resolvedCount = num(r.resolvedCount);
                const closedCount = num(r.closedCount, resolvedCount); // fallback to resolved
                const totalOverdue = num(r.totalOverdue); // open+closed overdue (API dependent)
                const avgClosedDays = num(r.avgClosingTimeDays);
                const avgOpenDays = num(r.avgOpenDays);

                const totalAssigned = Math.max(0, openCount + resolvedCount);

                // ---- OPEN ITEMS from this section (unresolved list respecting filters) ----
                const openItems = Array.isArray(section.items)
                  ? section.items
                  : [];
                const now = Date.now();

                // Sum open ages (days) + count open overdue
                const openAgesDaysTotal = openItems.reduce((sum, it) => {
                  const opened = getOpenStartDate(it);
                  if (!opened) return sum;
                  const ageMs = now - new Date(opened).getTime();
                  const ageDays = Number.isFinite(ageMs)
                    ? Math.max(0, ageMs / (24 * 3600e3))
                    : 0;
                  return sum + ageDays;
                }, 0);

                const openOverdueCount = openItems.reduce((acc, it) => {
                  const due = getDueAt(it);
                  return acc + (due && now > due.getTime() ? 1 : 0);
                }, 0);
                const openOnTimeCount = Math.max(
                  0,
                  openItems.length - openOverdueCount
                );

                // If API mixes open+closed overdue, split an estimate:
                const overdueClosed = Math.max(
                  0,
                  totalOverdue - openOverdueCount
                );
                const onTimeClosed = Math.max(0, closedCount - overdueClosed);

                // ---- COMBINED METRICS ----
                // Combined average (closed duration avg + current open age)
                const closedDaysTotal = avgClosedDays * closedCount; // avg * count
                const combinedDenom = Math.max(
                  0,
                  closedCount + openItems.length
                );
                const combinedAvgDays =
                  combinedDenom > 0
                    ? (closedDaysTotal + openAgesDaysTotal) / combinedDenom
                    : 0;

                // Overall SLA = (closed on time + open not overdue) / total assigned
                const slaOverallNum = onTimeClosed + openOnTimeCount;
                const slaOverallPctNum = pct(slaOverallNum, totalAssigned);
                const slaOverallPct = slaOverallPctNum.toFixed(1);

                // Simple distribution helpers
                const resolvedPct = pct(resolvedCount, totalAssigned).toFixed(
                  1
                );
                const openPct = pct(openCount, totalAssigned).toFixed(1);
                const overduePctAll = pct(totalOverdue, totalAssigned).toFixed(
                  1
                );

                // Efficiency: blend SLA with speed (lower avg days => higher score)
                const speedScore = Math.max(0, 100 - combinedAvgDays * 5);
                const efficiencyPct = (
                  (slaOverallPctNum / 100) *
                  speedScore
                ).toFixed(1);

                return (
                  <div className="flex flex-col items-start sm:items-end text-xs text-gray-700 gap-2 bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                    {/* Row 1: Distribution */}
                    <div className="flex flex-wrap gap-2">
                      <span className="rounded bg-green-100 px-2 py-0.5 text-green-800">
                        ✅ Resolved: {resolvedCount} ({resolvedPct}%)
                      </span>
                      <span className="rounded bg-yellow-100 px-2 py-0.5 text-yellow-800">
                        🕓 Open: {openCount} ({openPct}%)
                      </span>
                      {totalOverdue > 0 && (
                        <span className="rounded bg-red-100 px-2 py-0.5 text-red-800">
                          ⚠️ Overdue: {totalOverdue} ({overduePctAll}%)
                        </span>
                      )}
                    </div>

                    {/* Row 2: Averages */}
                    <div className="flex flex-wrap gap-3">
                      <span>
                        Avg Closed:{" "}
                        <span className="font-medium text-gray-900">
                          {avgClosedDays ? avgClosedDays.toFixed(2) : "—"} days
                        </span>
                      </span>
                      <span>
                        Avg Open Age:{" "}
                        <span className="font-medium text-gray-900">
                          {openItems.length
                            ? (openAgesDaysTotal / openItems.length).toFixed(2)
                            : avgOpenDays
                            ? avgOpenDays.toFixed?.(2) ?? avgOpenDays
                            : "—"}{" "}
                          days
                        </span>
                      </span>
                      <span>
                        Combined Avg:{" "}
                        <span className="font-medium text-gray-900">
                          {combinedAvgDays ? combinedAvgDays.toFixed(2) : "—"}{" "}
                          days
                        </span>
                      </span>
                    </div>

                    {/* Row 3: SLA + Efficiency */}
                    <div className="flex flex-wrap gap-3">
                      <span>
                        SLA (overall):{" "}
                        <span
                          className={`font-medium ${
                            slaOverallPctNum >= 80
                              ? "text-green-700"
                              : slaOverallPctNum >= 50
                              ? "text-yellow-600"
                              : "text-red-600"
                          }`}
                        >
                          {slaOverallPct}%
                        </span>
                      </span>
                      <span>
                        Efficiency:{" "}
                        <span className="font-semibold text-indigo-700">
                          {efficiencyPct}%
                        </span>
                      </span>
                    </div>

                    {/* Visual: SLA as green bar, overdue share as red overlay */}
                    <div className="w-full bg-gray-200 h-2 rounded-full mt-1 relative overflow-hidden">
                      <div
                        className="h-2 bg-green-500 rounded-full transition-all duration-500"
                        style={{ width: `${slaOverallPct}%` }}
                      />
                      <div
                        className="absolute top-0 left-0 h-2 bg-red-500/40 rounded-full transition-all duration-500"
                        style={{ width: `${overduePctAll}%` }}
                      />
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Table */}
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-100">
                <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                  <th className="px-4 py-3">Date (Opened)</th>
                  <th className="px-4 py-3">Opened For</th>
                  <th className="px-4 py-3">SLA Due</th>
                  <th className="px-4 py-3">Observer</th>
                  <th className="px-4 py-3">Area</th>
                  <th className="px-4 py-3">Risk</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {section.items.map((it, idx) => {
                  const opened = getOpenStartDate(it);
                  const due = getDueAt(it);
                  const isClosed = /closed|resolved/i.test(it?.status);
                  let slaBadge;

                  if (due) {
                    if (isClosed) {
                      const closedAt = new Date(
                        it?.closedOn ||
                          it?.closedAt ||
                          it?.resolvedAt ||
                          it?.updatedAt
                      );
                      if (closedAt && opened) {
                        const closedLate = closedAt.getTime() > due.getTime();
                        const duration =
                          closedAt.getTime() - new Date(opened).getTime();
                        slaBadge = (
                          <div className="flex flex-col">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                                closedLate
                                  ? "bg-red-100 text-red-700"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {closedLate ? "Closed Late" : "Closed On Time"}
                            </span>
                            <span className="text-[11px] text-gray-500 mt-1">
                              Took {formatDuration(duration)}
                            </span>
                          </div>
                        );
                      }
                    } else {
                      const overdue = Date.now() > due.getTime();
                      slaBadge = (
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            overdue
                              ? "bg-red-100 text-red-700"
                              : "bg-green-100 text-green-700"
                          }`}
                        >
                          {overdue ? "Overdue" : "On Time"}
                        </span>
                      );
                    }
                  } else slaBadge = "—";

                  return (
                    <tr
                      key={it._id}
                      onClick={() => openEdit(it)}
                      className={`cursor-pointer hover:bg-indigo-50 ${
                        idx % 2 ? "bg-gray-50/30" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3">
                        {opened ? new Date(opened).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {opened
                          ? isClosed
                            ? "—" // hide if closed
                            : formatDuration(
                                Date.now() - new Date(opened).getTime()
                              )
                          : "—"}
                      </td>

                      <td className="px-4 py-3">{slaBadge}</td>
                      <td className="px-4 py-3">{it?.observer?.name || "—"}</td>
                      <td className="px-4 py-3">{it?.area || "—"}</td>
                      <td className="px-4 py-3">
                        {it?.riskValue?.value
                          ? `${it?.riskValue?.value} (${
                              it?.riskValue?.severity || ""
                            })`
                          : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${
                            /closed/i.test(it?.status)
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {it?.status || "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))
      )}
    </div>
  );
}
